import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import styles from "./UserGroupCreateModal.module.scss";

const groupFormSchema = z.object({
  name: z
    .string()
    .min(2, "userGroups.validation.nameMin")
    .max(120, "userGroups.validation.nameMax"),
});

interface UserGroupCreateModalProps {
  isOpen: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: { name: string }) => void;
}

const UserGroupCreateModal: React.FC<UserGroupCreateModalProps> = ({
  isOpen,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const result = groupFormSchema.safeParse({ name: name.trim() });
    setIsFormValid(result.success);
  }, [name]);

  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setFieldErrors({});
  }, [isOpen]);

  const validateForm = () => {
    const result = groupFormSchema.safeParse({ name: name.trim() });
    if (result.success) {
      setFieldErrors({});
      return true;
    }
    const flattened = result.error.flatten().fieldErrors;
    setFieldErrors({
      name: flattened.name?.[0] ?? "",
    });
    return false;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit({ name: name.trim() });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("userGroups.createTitle")}
      size="md"
      withForm
      formId="userGroupCreateForm"
      handleSubmit={handleSubmit}
      isFormValid={isFormValid}
      loading={loading}
      submitButtonText={t("buttons.save")}
    >
      <form id="userGroupCreateForm" className={styles.form} onSubmit={handleSubmit}>
        <section className={styles.section}>
          <div>
            <h4 className={styles.sectionTitle}>{t("userGroups.form.sectionBasic")}</h4>
            <p className={styles.sectionSubtitle}>
              {t("userGroups.form.sectionBasicHint")}
            </p>
          </div>
          <Input
            label={t("userGroups.form.name")}
            placeholder={t("userGroups.form.namePlaceholder")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (fieldErrors.name) {
                setFieldErrors((prev) => ({ ...prev, name: "" }));
              }
            }}
            error={!!fieldErrors.name}
            errorMessage={fieldErrors.name}
            maxLength={120}
            required
            autoFocus
          />
        </section>
      </form>
    </Modal>
  );
};

export default UserGroupCreateModal;
